/**
 * The values a viewer chose in a board's controls: for each control the
 * page address holds a choice for, by control name, the value texts the
 * address holds. A control without an entry shows its default.
 */
export type ViewerControlChoices = ReadonlyMap<string, readonly string[]>;

/**
 * The choices of each board on screen in this tab, by board id, which the
 * board page sets from its address while it is mounted. The chat's check of
 * a proposed change reads them, so it runs each stage with the values the
 * viewer sees; a board that is not on screen has no entry.
 */
const choicesByBoard = new Map<string, ViewerControlChoices>();
const listeners = new Set<() => void>();

export function setViewerControlChoices(
  boardId: string,
  choices: ViewerControlChoices,
): void {
  choicesByBoard.set(boardId, choices);
  listeners.forEach((listener) => listener());
}

/** Forgets the choices of `boardId` once its page closes: the board is no
 *  longer on screen. */
export function clearViewerControlChoices(boardId: string): void {
  choicesByBoard.delete(boardId);
  listeners.forEach((listener) => listener());
}

/** The choices set for `boardId`, or undefined when it is not on screen. */
export function viewerControlChoicesFor(
  boardId: string,
): ViewerControlChoices | undefined {
  return choicesByBoard.get(boardId);
}

/** Calls `listener` after every change to any board's choices; returns the
 *  function that stops it. */
export function subscribeToViewerControlChoices(
  listener: () => void,
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
